import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { attachErrorSoundToToast } from '@/lib/errorSound';

// Globally play a buzzer sound whenever toast.error fires.
// This gives operators audible feedback during continuous scanning flows
// (Pressing, Picking, Put-Away, Damage, etc.) where a missed visual toast
// can cause them to keep scanning rejected pieces.
attachErrorSoundToToast(toast);
import USNReplacementDialog from '@/components/USNReplacementDialog';
import Login from "@/pages/Login";
import SiteSelect from "@/pages/SiteSelect";
import SiteManagement from "@/pages/Admin/SiteManagement";
import AdminWarehouses from "@/pages/Admin/Warehouses";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import HomePage from "@/pages/HomePage";
import InwardForm from "@/pages/GateEntry/InwardForm";
import OutwardForm from "@/pages/GateEntry/OutwardForm";
import OutwardTransportReturns from "@/pages/GateEntry/OutwardTransportReturns";
import CustomerOutwardEntry from "@/pages/GateEntry/CustomerOutwardEntry";
import PrintCodes from "@/pages/PrintCodes";
import PrinterSetup from "@/pages/PrinterSetup";
import PrintManager from "@/pages/PrintManager";
import Messenger from "@/pages/Messenger";
import TransactionDashboard from "@/pages/TransactionDashboard";
import InwardDocumentation from "@/pages/InwardDocumentation";
import OutwardDocumentation from "@/pages/OutwardDocumentation";
import CustomerOutwardDocs from "@/pages/CustomerOutwardDocs";
import MyEntries from "@/pages/MyEntries";
import DraftEntries from "@/pages/DraftEntries";
import Notifications from "@/pages/Notifications";
import IncomingConsignments from "@/pages/IncomingConsignments";
import ShipmentRequests from "@/pages/ShipmentRequests";
import AdminDashboard from "@/pages/Admin/Dashboard";
import DcNotificationSettings from "@/pages/Admin/DcNotificationSettings";
import UserManagement from "@/pages/Admin/UserManagement";
import ImportData from "@/pages/Admin/ImportData";
import ManageImports from "@/pages/Admin/ManageImports";
import DatabaseManagement from "@/pages/Admin/DatabaseManagement";
import ReassignLocation from "@/pages/Admin/ReassignLocation";
import InwardReports from "@/pages/Admin/InwardReports";
import OutwardReports from "@/pages/Admin/OutwardReports";
import CustomerOutwardReports from "@/pages/Admin/CustomerOutwardReports";
import TransporterReturns from "@/pages/Admin/TransporterReturns";
import GoodsTransferReports from "@/pages/Admin/GoodsTransferReports";
import FOBReports from "@/pages/Admin/FOBReports";
import FOBEntryManagement from "@/pages/Admin/FOBEntryManagement";
import BackupRestore from "@/pages/Admin/BackupRestore";
import GoodsTransferManagement from "@/pages/Admin/GoodsTransferManagement";
import InwardEntryManagement from "@/pages/Admin/InwardEntryManagement";
import OutwardEntryManagement from "@/pages/Admin/OutwardEntryManagement";
import TransportMaster from "@/pages/Admin/TransportMaster";
import GreigeSectionEntryManagement from "@/pages/Admin/GreigeSectionEntryManagement";
import LabelConfig from "@/pages/Admin/LabelConfig";
import MainLabelTemplates from "@/pages/Admin/MainLabelTemplates";
import OperationsManager from "@/pages/Admin/OperationsManager";
import Payslip from "@/pages/Admin/Payslip";
import HourlyStaffReport from "@/pages/Sections/Accounts/HourlyStaffReport";
import OperationRankings from "@/pages/Sections/Accounts/OperationRankings";
import SectionITReport from "@/pages/Sections/SectionITReport";
import SectionDocumentsReport from "@/pages/Sections/SectionDocumentsReport";
import FOBPackedNotTransferredReport from "@/pages/Sections/FOB/PackedNotTransferredReport";
import PrintStations from "@/pages/Admin/PrintStations";
import { LabelPrinterProvider } from "@/contexts/LabelPrinterProvider";
import PrintSettings from "@/pages/Admin/PrintSettings";
import WMSReports from "@/pages/Admin/WMSReports";
import SystemDocumentation from "@/pages/Admin/SystemDocumentation";
import USNMasterSearch from "@/pages/Admin/USNMasterSearch";
import USNMasterExport from "@/pages/Admin/USNMasterExport";
import AllUSNDetailsReport from "@/pages/Admin/AllUSNDetailsReport";
import PrinterSettings from "@/pages/Admin/PrinterSettings";
import DispatchReportsAdmin from "@/pages/Admin/DispatchReportsAdmin";
import InternalTransferReport from "@/pages/Reports/InternalTransferReport";
import OperationWiseStockReport from "@/pages/Reports/OperationWiseStockReport";
import StorageAgeingReport from "@/pages/Reports/StorageAgeingReport";
import DwellTimeReport from "@/pages/Reports/DwellTimeReport";
import FetchDocumentNumber from "@/pages/Reports/FetchDocumentNumber";
import USNTracker from "@/pages/Admin/USNTracker";
import USNReplacementLog from "@/pages/Admin/USNReplacementLog";
import InhouseBundleReconciliation from "@/pages/Admin/InhouseBundleReconciliation";
import FOBDashboard from "@/pages/Sections/FOB/Dashboard";
import FOBIncomingConsignment from "@/pages/Sections/FOB/IncomingConsignment";
import BundleReceiptEntry from "@/pages/Sections/FOB/BundleReceiptEntry";
import SortNoUsnTagging from "@/pages/Sections/FOB/SortNoUsnTagging";
import FOBAssortmentPacking from "@/pages/Sections/FOB/AssortmentPacking";
import FOBForceClose from "@/pages/Sections/FOB/FOBForceClose";
import FOBDamageReport from "@/pages/Sections/FOB/FOBDamageReport";
import USNReprint from "@/pages/Sections/FOB/USNReprint";
import InternalTransfer from "@/pages/Sections/FOB/InternalTransfer";
import FOBSectionReport from "@/pages/Sections/FOB/Report";
import FOBClosingStockReport from "@/pages/Sections/FOB/ClosingStockReport";
import TransferRequests from "@/pages/Sections/TransferRequests";
import ShippedTransfers from "@/pages/Sections/ShippedTransfers";
import GreigeDashboard from "@/pages/Sections/Greige/Dashboard";
import GreigeIncomingConsignment from "@/pages/Sections/Greige/IncomingConsignment";
import GreigeBundleReceiptEntry from "@/pages/Sections/Greige/BundleReceiptEntry";
import GreigePinning from "@/pages/Sections/Greige/Pinning";
import GreigeMarkerWriting from "@/pages/Sections/Greige/MarkerWriting";
import GreigePacking from "@/pages/Sections/Greige/Packing";
import BundleSplit from "@/pages/Sections/Greige/BundleSplit";
import SortAssignment from "@/pages/Sections/Greige/SortAssignment";
import GreigeInspection from "@/pages/Sections/Greige/GreigeInspection";
import GreigeReconcile from "@/pages/Sections/Greige/Reconcile";
import GreigeReconcileReport from "@/pages/Sections/Greige/ReconcileReport";
import LegacyBundleBackfill from "@/pages/Sections/Greige/LegacyBundleBackfill";
import GreigeInternalTransfer from "@/pages/Sections/Greige/InternalTransfer";
import GreigeSectionReport from "@/pages/Sections/Greige/Report";
import GreigeSectionClosingStock from "@/pages/Sections/Greige/ClosingStockReport";
import GreigeShipmentRequests from "@/pages/Sections/Greige/ShipmentRequests";
import BundleUSNTrace from "@/pages/Sections/Greige/BundleUSNTrace";
import GreigeBundleDetailReport from "@/pages/Sections/Greige/BundleDetailReport";
import GreigeDocumentsReport from "@/pages/Sections/Greige/DocumentsReport";
import FOBShipmentRequests from "@/pages/Sections/FOB/ShipmentRequests";
import GoodsTransferShipmentRequests from "@/pages/Sections/GoodsTransfer/ShipmentRequests";
import GoodsTransferIncomingConsignment from "@/pages/Sections/GoodsTransfer/IncomingConsignment";
import { GoodsTransferDashboard, GoodsTransferBundleReceiptEntry, Packing as GTPacking, SortNoAssignment, FGAssignment, LocationAssignment, MainLabelPrinting, PrintJobHistory, GoodsTransferReport, GoodsTransferClosingStock, LabelReports, GoodsTransferInternalTransfer, LocationTransfer as GTLocationTransfer } from "@/pages/Sections/GoodsTransfer";
import { WMSDashboard, SFGWMSDashboard, FGWMSDashboard, LocationSetup, ZoneMapping, SFGPutAway, Projections, PickSlips, PickerGuidance, ReplenishmentDashboard, WMSSettings, FGPutAway, FGPickSlips, FGPickSlipUpload, SeasonalPickSlipUpload, SeasonalPackingReport, UploadedPickSlipsReport, SeasonalPendingPackingReport, FGPackedHistory,
  SeasonalBundleDashboard, SeasonalLocationSetup, SeasonalPackedBoxPutAway, SeasonalCreatePickSlip, SeasonalAvailablePickers, SeasonalMobilePicking, SeasonalLocationStockReport, SeasonalBoxPickSlips, SeasonalMasterCartonHistory, FGPickerGuidance, MobileWMSDashboard, MobileSFGPutAway, MobileSFGPicking, MobileFGPutAway, MobileFGPicking, DesignTransfer, DispatchSupervisor, PickerDashboard, SFGMainLabelPrinting, FGPutAwaySupervisor, FGPutAwayMobile, FGInventoryReport, PutAwayEntriesReport, SFGInventoryReport, SFGPutAwayEntriesReport, SFGCreatePickSlip, PackingDashboard, PackingWorkstation, MobilePickAndPack, PickPackLiveBoard, OrderManagementDashboard, BinToBinTransfer, FGDesignTransfer, FGDesignTransferReport, BundleHistory, CancelledPickSlipPutAway, FGUSNLabelReprint, BulkRestickerStaging, FGDesignTransferSlips, DesignTransferPicker, StaffRankings, StaffRankingsTV, ComboBOMaster, ComboAssemblyStation, ComboReversal, ComboBundleTrace, FootprintCapacity, InWIPStockReport, InWIPBundleStatusReport, SFGFloorClearance, SFGDesignTransfer, WMSStorageTransfer, MobileStaffRankings, FGPutAwaySlipManagement, FGPutAwayClearance, FOBDirectFGPutAway, FGCreatePack, SmallBoxPutAway, SFGInvestigationQueue, FGInvestigationQueue, USNLookup, FGBinAudit, SFGBinToBinTransfer, WIPDamageClearance, DamageSectionReceipt, DamageInventoryReport } from "@/pages/WMS";
import { GreigeStorageDashboard, GSIncomingConsignment, GSBundleReceiptEntry, GSLocationPutAway, GSLocationTransfer, GSShipmentRequest, GSPickSlipGeneration, GSPicking, GSSortNoTransfer, GSInternalTransfer, GSReport, GSLocationSetup, GSClosingStockReport, GSBundleTracker, GSDocTrack, GSAssortmentPickSlip, GSAssortmentPicking, GSAvailablePickers, GSLocationStockReport, GSInternalTransferReport, GSAssortmentConversionReport, GSPickedNotTransferredReport } from "@/pages/Sections/GreigeStorage";
import { ProcessingDashboard, ProcessingInwardConsignment, ProcessingShipmentRequests, ProcessingInternalTransfer, ProcessingReport, ProcessingClosingStock } from "@/pages/Sections/Processing";
import { CategoryInchargeDashboard, CategoryManagerDashboard, ShipmentRequestsSection, MyShipmentRequests } from "@/pages/Category";
import StaffPerformanceDashboard from "@/pages/StaffPerformanceDashboard";

// Combo V2 — Bulk Assembly (parallel flow)
import ComboV2Dashboard from "@/pages/WMS/ComboV2/Dashboard";
import ComboV2Planner from "@/pages/WMS/ComboV2/Planner";
import ComboV2PlanList from "@/pages/WMS/ComboV2/PlanList";
import ComboV2PlanDetail from "@/pages/WMS/ComboV2/PlanDetail";
import ComboV2PlanPrint from "@/pages/WMS/ComboV2/PlanPrint";
import ComboV2DistributorPlanList from "@/pages/WMS/ComboV2/DistributorList";
import ComboV2DistributorStation from "@/pages/WMS/ComboV2/DistributorStation";
import ComboV2PPStation from "@/pages/WMS/ComboV2/PPStation";
import ComboV2StickerPrintDesk from "@/pages/WMS/ComboV2/StickerPrintDesk";
import ComboV2ReturnExcess from "@/pages/WMS/ComboV2/ReturnExcess";
import ComboV2Reversal from "@/pages/WMS/ComboV2/Reversal";

// Combo Reconciliation (Floor wrap-up + Successful sessions + Variance)
import ComboFloorReconciliation from "@/pages/WMS/ComboFloorReconciliation";
import ComboSuccessfulSessions from "@/pages/WMS/ComboSuccessfulSessions";
import ComboPPStaffPerformance from "@/pages/WMS/ComboPPStaffPerformance";
import ComboVarianceReconciliation from "@/pages/WMS/ComboVarianceReconciliation";

// Purchase Orders module
import POList from "@/pages/PO/POList";
import POCreate from "@/pages/PO/POCreate";
import PODetail from "@/pages/PO/PODetail";
import POFollowUp from "@/pages/PO/POFollowUp";
import GrnPage from "@/pages/PO/GrnPage";
import PRList from "@/pages/PR/PRList";
import PRCreate from "@/pages/PR/PRCreate";
import PRDetail from "@/pages/PR/PRDetail";
import PRPrint from "@/pages/PR/PRPrint";

// New Section Imports - 16 Additional Sections
import InhouseDashboard from "@/pages/Sections/Inhouse/Dashboard";
import InhouseInternalTransfer from "@/pages/Sections/Inhouse/InternalTransfer";
import InhouseInwardConsignment from "@/pages/Sections/Inhouse/InwardConsignment";
import InhouseShipmentRequest from "@/pages/Sections/Inhouse/ShipmentRequest";
import InhouseBundleReceiptEntry from "@/pages/Sections/Inhouse/BundleReceiptEntry";
import InhouseQtyConfirmation from "@/pages/Sections/Inhouse/QtyConfirmation";
import ProcessingInward from "@/pages/Sections/Inhouse/ProcessingInward";
import ProcessingInwardHistory from "@/pages/Sections/Inhouse/ProcessingInwardHistory";
import Folding from "@/pages/Sections/Inhouse/Folding";
import FoldingHistory from "@/pages/Sections/Inhouse/FoldingHistory";
import Segregation from "@/pages/Sections/Inhouse/Segregation";
import Pressing from "@/pages/Sections/Inhouse/Pressing";
import PressingHistory from "@/pages/Sections/Inhouse/PressingHistory";
import DamageChecking from "@/pages/Sections/Inhouse/DamageChecking";
import JobWorkerReturn from "@/pages/Sections/Inhouse/JobWorkerReturn";
import AssortmentPacking from "@/pages/Sections/Inhouse/AssortmentPacking";
import InhouseDesignTransfer from "@/pages/Sections/Inhouse/DesignTransfer";
import AssortmentShip from "@/pages/Sections/Inhouse/AssortmentShip";
import DirectShipHistory from "@/pages/Sections/Inhouse/DirectShipHistory";
import ReturnToSupplierDocs from "@/pages/Sections/Inhouse/ReturnToSupplierDocs";
import InhouseUSNReport from "@/pages/Sections/Inhouse/InhouseUSNReport";
import InhouseOperationsReport from "@/pages/Sections/Inhouse/InhouseOperationsReport";
import ReFoldingEntry from "@/pages/Sections/Inhouse/ReFoldingEntry";
import ColorAssortmentEntry from "@/pages/Sections/Inhouse/ColorAssortmentEntry";
import InhouseCreateJobOrder from "@/pages/Sections/Inhouse/CreateJobOrder";
import InhouseJobberReport from "@/pages/Sections/Inhouse/JobberReport";
import InhouseReturnChecks from "@/pages/Sections/Inhouse/ReturnChecks";
import FOBUSNReport from "@/pages/Sections/FOB/USNReport";

// Internal Transport (Phase 1 MVP)
import TransportDashboard from "@/pages/Sections/Transport/Dashboard";
import TransportMyTasks from "@/pages/Sections/Transport/MyTasks";
import TransportRequestBoard from "@/pages/Sections/Transport/RequestBoard";
import TransportRequestVehicle from "@/pages/Sections/Transport/RequestVehicle";
import TransportTrips from "@/pages/Sections/Transport/Trips";
import TransportFuelLog from "@/pages/Sections/Transport/FuelLog";
import TransportServiceLog from "@/pages/Sections/Transport/ServiceLog";
import TransportLocations from "@/pages/Sections/Transport/Locations";
import TransportDispatchBoard from "@/pages/Sections/Transport/DispatchBoard";
import TransportAttendance from "@/pages/Sections/Transport/Attendance";
import StaffAttendanceHub from "@/pages/StaffAttendance/Hub";
import EwayBillsPage from "@/pages/EwayBills/EwayBillsPage";
import SuppliersPage from "@/pages/EwayBills/SuppliersPage";
import PtFilesPage from "@/pages/EwayBills/PtFilesPage";
import MyStaffAttendance from "@/pages/StaffAttendance/MyAttendance";
import AttendanceKiosk from "@/pages/StaffAttendance/Kiosk";
import MyPayPortal from "@/pages/MyPay/MyPayPortal";
import StaffMaster from "@/pages/Admin/StaffMaster";
import JobberMaster from "@/pages/Admin/JobberMaster";
import HeadcountTV from "@/pages/StaffAttendance/HeadcountTV";
import TransportVehicles from "@/pages/Sections/Transport/Vehicles";
import TransportDrivers from "@/pages/Sections/Transport/Drivers";
import TransportReports from "@/pages/Sections/Transport/Reports";
import TransportSettings from "@/pages/Sections/Transport/Settings";

import UnassortedDashboard from "@/pages/Sections/Unassorted/Dashboard";
import UnassortedInternalTransfer from "@/pages/Sections/Unassorted/InternalTransfer";
import UnassortedInwardConsignment from "@/pages/Sections/Unassorted/InwardConsignment";
import UnassortedShipmentRequest from "@/pages/Sections/Unassorted/ShipmentRequest";
import UnassortedBundleReceiptEntry from "@/pages/Sections/Unassorted/BundleReceiptEntry";

import TissueDashboard from "@/pages/Sections/Tissue/Dashboard";
import TissueInternalTransfer from "@/pages/Sections/Tissue/InternalTransfer";
import TissueInwardConsignment from "@/pages/Sections/Tissue/InwardConsignment";
import TissueShipmentRequest from "@/pages/Sections/Tissue/ShipmentRequest";
import TissueBundleReceiptEntry from "@/pages/Sections/Tissue/BundleReceiptEntry";

import FlexiDashboard from "@/pages/Sections/Flexi/Dashboard";
import FlexiInternalTransfer from "@/pages/Sections/Flexi/InternalTransfer";
import FlexiInwardConsignment from "@/pages/Sections/Flexi/InwardConsignment";
import FlexiShipmentRequest from "@/pages/Sections/Flexi/ShipmentRequest";
import FlexiBundleReceiptEntry from "@/pages/Sections/Flexi/BundleReceiptEntry";
import FlexiFGAssignment from "@/pages/Sections/Flexi/FGAssignment";

import ShirtManufacturingDashboard from "@/pages/Sections/ShirtManufacturing/Dashboard";
import ShirtManufacturingInternalTransfer from "@/pages/Sections/ShirtManufacturing/InternalTransfer";
import ShirtManufacturingInwardConsignment from "@/pages/Sections/ShirtManufacturing/InwardConsignment";
import ShirtManufacturingShipmentRequest from "@/pages/Sections/ShirtManufacturing/ShipmentRequest";
import ShirtManufacturingBundleReceiptEntry from "@/pages/Sections/ShirtManufacturing/BundleReceiptEntry";
import ShirtMfgHub from "@/pages/Sections/ShirtManufacturing/ShirtMfgHub";
import ShirtMfgJwTaggingStation from "@/pages/Sections/ShirtManufacturing/JwTaggingStation";
import ShirtsAssortment from "@/pages/Sections/ShirtManufacturing/ShirtsAssortment";
import ShirtMfgBundleSplit from "@/pages/Sections/ShirtManufacturing/BundleSplitStation";
import ShirtMfgQtyInspection from "@/pages/Sections/ShirtManufacturing/QtyInspection";
import ShirtMfgVarianceReport from "@/pages/Sections/ShirtManufacturing/VarianceReport";

import SampleDashboard from "@/pages/Sections/Sample/Dashboard";
import SampleInternalTransfer from "@/pages/Sections/Sample/InternalTransfer";
import SampleInwardConsignment from "@/pages/Sections/Sample/InwardConsignment";
import SampleShipmentRequest from "@/pages/Sections/Sample/ShipmentRequest";
import SampleBundleReceiptEntry from "@/pages/Sections/Sample/BundleReceiptEntry";

import SalesReturnDashboard from "@/pages/Sections/SalesReturn/Dashboard";
import SalesReturnInternalTransfer from "@/pages/Sections/SalesReturn/InternalTransfer";
import SalesReturnInwardConsignment from "@/pages/Sections/SalesReturn/InwardConsignment";
import SalesReturnShipmentRequest from "@/pages/Sections/SalesReturn/ShipmentRequest";
import SalesReturnBundleReceiptEntry from "@/pages/Sections/SalesReturn/BundleReceiptEntry";
import SalesReturnTransactions from "@/pages/Sections/SalesReturn/Transactions";
import SalesReturnRefinishBundleManager from "@/pages/Sections/SalesReturn/RefinishBundleManager";
import SamplePickSlipUpload from "@/pages/Sections/Sample/PickSlipUpload";
import SampleCuttingStation from "@/pages/Sections/Sample/CuttingStation";
import SampleFullSamplePacking from "@/pages/Sections/Sample/FullSamplePacking";
import FlexiHub from "@/pages/Sections/Flexi/FlexiHub";
import FlexiJwTaggingStation from "@/pages/Sections/Flexi/JwTaggingStation";
import FlexiJwReceiptStation from "@/pages/Sections/Flexi/JwReceiptStation";
import FlexiJwOrderReport from "@/pages/Sections/Flexi/JwOrderReport";
import FlexiAssortment from "@/pages/Sections/Flexi/FlexiAssortment";
import TissueHub from "@/pages/Sections/Tissue/TissueHub";
import TissueJwTaggingStation from "@/pages/Sections/Tissue/JwTaggingStation";
import TissueAssortment from "@/pages/Sections/Tissue/TissueAssortment";
import TissueJwOrderDetail from "@/pages/Sections/Tissue/JwOrderDetail";
import GSTissueJwPicking from "@/pages/Sections/GreigeStorage/TissueJwPicking";
import GSProcessingJobOrders from "@/pages/Sections/GreigeStorage/ProcessingJobOrders";
import ProcessingJobOrders from "@/pages/Sections/Processing/JobOrders";
import ProcessingJobOrderDetail from "@/pages/Sections/Processing/JobOrderDetail";
import ProcessingJobOrderReports from "@/pages/Sections/Processing/JobOrderReports";

import AccountsDashboard from "@/pages/Sections/Accounts/Dashboard";
import AccountsInternalTransfer from "@/pages/Sections/Accounts/InternalTransfer";
import AccountsInwardConsignment from "@/pages/Sections/Accounts/InwardConsignment";
import AccountsShipmentRequest from "@/pages/Sections/Accounts/ShipmentRequest";
import AccountsBundleReceiptEntry from "@/pages/Sections/Accounts/BundleReceiptEntry";
import AccountsHRTile from "@/pages/Sections/Accounts/HRTile";
import AccountsGateEntryDetails from "@/pages/Sections/Accounts/GateEntryDetails";
import OutwardGateEntryDetails from "@/pages/Sections/Accounts/OutwardGateEntryDetails";
import FoldingCategoryWages from "@/pages/HR/FoldingCategoryWages";

import TrimsDashboard from "@/pages/Sections/Trims/Dashboard";
import TrimsInternalTransfer from "@/pages/Sections/Trims/InternalTransfer";
import TrimsInwardConsignment from "@/pages/Sections/Trims/InwardConsignment";
import TrimsShipmentRequest from "@/pages/Sections/Trims/ShipmentRequest";
import TrimsBundleReceiptEntry from "@/pages/Sections/Trims/BundleReceiptEntry";
import SectionPurchaseReceipt from "@/pages/Sections/PurchaseReceipt";
import SupplierReturns from "@/pages/Sections/SupplierReturns";

import HouseKeepingDashboard from "@/pages/Sections/HouseKeeping/Dashboard";
import HouseKeepingInternalTransfer from "@/pages/Sections/HouseKeeping/InternalTransfer";
import HouseKeepingInwardConsignment from "@/pages/Sections/HouseKeeping/InwardConsignment";
import HouseKeepingShipmentRequest from "@/pages/Sections/HouseKeeping/ShipmentRequest";
import HouseKeepingBundleReceiptEntry from "@/pages/Sections/HouseKeeping/BundleReceiptEntry";

import ElectricalGoodsDashboard from "@/pages/Sections/ElectricalGoods/Dashboard";
import ElectricalGoodsInternalTransfer from "@/pages/Sections/ElectricalGoods/InternalTransfer";
import ElectricalGoodsInwardConsignment from "@/pages/Sections/ElectricalGoods/InwardConsignment";
import ElectricalGoodsShipmentRequest from "@/pages/Sections/ElectricalGoods/ShipmentRequest";
import ElectricalGoodsBundleReceiptEntry from "@/pages/Sections/ElectricalGoods/BundleReceiptEntry";

import ITGoodsDashboard from "@/pages/Sections/ITGoods/Dashboard";
import ITGoodsInternalTransfer from "@/pages/Sections/ITGoods/InternalTransfer";
import ITGoodsInwardConsignment from "@/pages/Sections/ITGoods/InwardConsignment";
import ITGoodsShipmentRequest from "@/pages/Sections/ITGoods/ShipmentRequest";
import ITGoodsBundleReceiptEntry from "@/pages/Sections/ITGoods/BundleReceiptEntry";

import RnMDashboard from "@/pages/Sections/RnM/Dashboard";
import RnMInternalTransfer from "@/pages/Sections/RnM/InternalTransfer";
import RnMInwardConsignment from "@/pages/Sections/RnM/InwardConsignment";
import RnMShipmentRequest from "@/pages/Sections/RnM/ShipmentRequest";
import RnMBundleReceiptEntry from "@/pages/Sections/RandM/BundleReceiptEntry";

import ECOMDashboard from "@/pages/Sections/ECOM/Dashboard";
import ECOMInternalTransfer from "@/pages/Sections/ECOM/InternalTransfer";
import ECOMInwardConsignment from "@/pages/Sections/ECOM/InwardConsignment";
import ECOMShipmentRequest from "@/pages/Sections/ECOM/ShipmentRequest";
import ECOMBundleReceiptEntry from "@/pages/Sections/ECOM/BundleReceiptEntry";

import AdminSectionDashboard from "@/pages/Sections/AdminSection/Dashboard";
import AdminSectionInternalTransfer from "@/pages/Sections/AdminSection/InternalTransfer";
import AdminSectionInwardConsignment from "@/pages/Sections/AdminSection/InwardConsignment";
import AdminSectionShipmentRequest from "@/pages/Sections/AdminSection/ShipmentRequest";
import AdminSectionBundleReceiptEntry from "@/pages/Sections/AdminSection/BundleReceiptEntry";

import WeavingDashboard from "@/pages/Sections/Weaving/Dashboard";
import WeavingInternalTransfer from "@/pages/Sections/Weaving/InternalTransfer";
import WeavingInwardConsignment from "@/pages/Sections/Weaving/InwardConsignment";
import WeavingShipmentRequest from "@/pages/Sections/Weaving/ShipmentRequest";
import WeavingBundleReceiptEntry from "@/pages/Sections/Weaving/BundleReceiptEntry";

import YarnDashboard from "@/pages/Sections/Yarn/Dashboard";
import YarnInternalTransfer from "@/pages/Sections/Yarn/InternalTransfer";
import YarnInwardConsignment from "@/pages/Sections/Yarn/InwardConsignment";
import YarnShipmentRequest from "@/pages/Sections/Yarn/ShipmentRequest";
import YarnBundleReceiptEntry from "@/pages/Sections/Yarn/BundleReceiptEntry";

import DispatchDashboard from "@/pages/Sections/Dispatch/Dashboard";
import PerSectionDispatchHub from "@/pages/Sections/DispatchPerSection/PerSectionDispatchHub";
import PerSectionStub from "@/pages/Sections/DispatchPerSection/PerSectionStub";
import PerSectionBundleReceiptEntryClone from "@/pages/Sections/DispatchPerSection/PerSectionBundleReceiptEntryClone";
import PerSectionPiecesProcessedClone from "@/pages/Sections/DispatchPerSection/PerSectionPiecesProcessedClone";
import PerSectionInwardConsignmentClone from "@/pages/Sections/DispatchPerSection/PerSectionInwardConsignmentClone";
import PerSectionInternalTransferClone from "@/pages/Sections/DispatchPerSection/PerSectionInternalTransferClone";
import PerSectionSfgPutAwayClone from "@/pages/Sections/DispatchPerSection/PerSectionSfgPutAwayClone";
import PerSectionSfgPickClone from "@/pages/Sections/DispatchPerSection/PerSectionSfgPickClone";
import PerSectionFgPutAwayClone from "@/pages/Sections/DispatchPerSection/PerSectionFgPutAwayClone";
import PerSectionFgPickClone from "@/pages/Sections/DispatchPerSection/PerSectionFgPickClone";
import PerSectionComboV2Clone from "@/pages/Sections/DispatchPerSection/PerSectionComboV2Clone";
import PerSectionComboV2Planner from "@/pages/Sections/DispatchPerSection/PerSectionComboV2Planner";
import PerSectionPpStationClone from "@/pages/Sections/DispatchPerSection/PerSectionPpStationClone";
import PerSectionReturnExcessClone from "@/pages/Sections/DispatchPerSection/PerSectionReturnExcessClone";
import PerSectionReversalClone from "@/pages/Sections/DispatchPerSection/PerSectionReversalClone";
import PerSectionMobileDashboard from "@/pages/Sections/DispatchPerSection/PerSectionMobileDashboard";
import PerSectionMobileSfgPutAway from "@/pages/Sections/DispatchPerSection/PerSectionMobileSfgPutAway";
import PerSectionMobileSfgPick from "@/pages/Sections/DispatchPerSection/PerSectionMobileSfgPick";
import PerSectionMobileFgPutAway from "@/pages/Sections/DispatchPerSection/PerSectionMobileFgPutAway";
import PerSectionMobileFgPick from "@/pages/Sections/DispatchPerSection/PerSectionMobileFgPick";
import PerSectionSfgWmsLanding from "@/pages/Sections/DispatchPerSection/PerSectionSfgWmsLanding";
import PerSectionFgWmsLanding from "@/pages/Sections/DispatchPerSection/PerSectionFgWmsLanding";
import PerSectionShipmentRequest from "@/pages/Sections/DispatchPerSection/PerSectionShipmentRequest";
import PerSectionFootprintMovementReport from "@/pages/Sections/DispatchPerSection/PerSectionFootprintMovementReport";
import PerSectionDispatchSupervisor from "@/pages/Sections/DispatchPerSection/PerSectionDispatchSupervisor";
import PerSectionLocationSetup from "@/pages/Sections/DispatchPerSection/PerSectionLocationSetup";
import PerSectionZoneMapping from "@/pages/Sections/DispatchPerSection/PerSectionZoneMapping";
import PerSectionWMSSettings from "@/pages/Sections/DispatchPerSection/PerSectionWMSSettings";
import PerSectionFootprintCapacity from "@/pages/Sections/DispatchPerSection/PerSectionFootprintCapacity";
import PerSectionBinToBinTransfer from "@/pages/Sections/DispatchPerSection/PerSectionBinToBinTransfer";
import PerSectionSFGBinToBinTransfer from "@/pages/Sections/DispatchPerSection/PerSectionSFGBinToBinTransfer";
import PerSectionFGBinAudit from "@/pages/Sections/DispatchPerSection/PerSectionFGBinAudit";
import ReStickerQueue from "@/pages/ReStickerQueue";
import DispatchInternalTransfer from "@/pages/Sections/Dispatch/InternalTransfer";
import DispatchInwardConsignment from "@/pages/Sections/Dispatch/InwardConsignment";
import DispatchShipmentRequest from "@/pages/Sections/Dispatch/ShipmentRequest";
import DispatchBundleReceiptEntry from "@/pages/Sections/Dispatch/BundleReceiptEntry";
import DispatchPiecesProcessed from "@/pages/Sections/Dispatch/PiecesProcessed";
import DispatchPiecesProcessedTV from "@/pages/Sections/Dispatch/PiecesProcessedTV";
import DispatchUSNTracking from "@/pages/Sections/Dispatch/USNTracking";
import BundleReceiptReport from "@/pages/WMS/BundleReceiptReport";
import DispatchInternalTransferReport from "@/pages/Sections/Dispatch/InternalTransferReport";
import FootprintMovementReport from "@/pages/Sections/Dispatch/FootprintMovementReport";
import TrafficControlDashboard from "@/pages/Sections/Dispatch/TrafficControlDashboard";
import MiscOutward from "@/pages/Sections/MiscOutward";
import SectionInventoryReport from "@/pages/Sections/SectionInventoryReport";

import ProtectedRoute from "@/components/ProtectedRoute";
import TransporterPortal from "@/pages/TransporterPortal";
import usePresenceHeartbeat from "@/hooks/usePresenceHeartbeat";
import useMessageNotifications from "@/hooks/useMessageNotifications";
import "@/App.css";

// Router component that handles session_id detection
function AppRouter() {
  const location = useLocation();

  // Keep any logged-in user ONLINE app-wide (not only on the Messenger page).
  usePresenceHeartbeat();
  // WhatsApp-style new-message notifications on EVERY page (toast + desktop + chime).
  useMessageNotifications();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <Routes>
      {/* Public Routes */}
      <Route path="/select-site" element={<SiteSelect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/transporter-portal/:token" element={<TransporterPortal />} />
      <Route path="/attendance/kiosk" element={<AttendanceKiosk />} />
      <Route path="/my-pay" element={<MyPayPortal />} />
      <Route path="/attendance" element={<ProtectedRoute><StaffAttendanceHub /></ProtectedRoute>} />
      <Route path="/eway-bills" element={<ProtectedRoute><EwayBillsPage /></ProtectedRoute>} />
      <Route path="/eway-bills/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
      <Route path="/eway-bills/pt-files" element={<ProtectedRoute><PtFilesPage /></ProtectedRoute>} />
      <Route path="/attendance/my" element={<ProtectedRoute><MyStaffAttendance /></ProtectedRoute>} />
      <Route path="/admin/staff-master" element={<ProtectedRoute><StaffMaster /></ProtectedRoute>} />
      <Route path="/admin/jobber-master" element={<ProtectedRoute><JobberMaster /></ProtectedRoute>} />
      <Route path="/attendance/headcount-tv" element={<ProtectedRoute><HeadcountTV /></ProtectedRoute>} />
      <Route path="/admin/site-management" element={<ProtectedRoute><SiteManagement /></ProtectedRoute>} />
      <Route path="/admin/warehouses" element={<ProtectedRoute><AdminWarehouses /></ProtectedRoute>} />
      
      {/* Protected User Routes */}
      <Route
        path="/dashboard"
        element={<Navigate to="/admin/dashboard" replace />}
      />
      <Route
        path="/"
        element={<Navigate to="/select-site" replace />}
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gate-entry/inward"
        element={
          <ProtectedRoute>
            <InwardForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gate-entry/outward"
        element={
          <ProtectedRoute>
            <OutwardForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gate-entry/outward-returns"
        element={
          <ProtectedRoute>
            <OutwardTransportReturns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gate-entry/outward-customer"
        element={
          <ProtectedRoute>
            <CustomerOutwardEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/print-codes"
        element={
          <ProtectedRoute>
            <PrintCodes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/printer-setup"
        element={
          <ProtectedRoute>
            <PrinterSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/print-manager"
        element={
          <ProtectedRoute>
            <PrintManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messenger"
        element={
          <ProtectedRoute>
            <Messenger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transaction-dashboard"
        element={
          <ProtectedRoute>
            <TransactionDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation"
        element={
          <ProtectedRoute>
            <InwardDocumentation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outward-documentation"
        element={
          <ProtectedRoute>
            <OutwardDocumentation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer-outward-documentation"
        element={
          <ProtectedRoute>
            <CustomerOutwardDocs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-entries"
        element={
          <ProtectedRoute>
            <MyEntries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drafts"
        element={
          <ProtectedRoute>
            <DraftEntries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/draft-entries"
        element={
          <ProtectedRoute>
            <DraftEntries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/incoming-consignments"
        element={
          <ProtectedRoute>
            <IncomingConsignments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipment-requests"
        element={
          <ProtectedRoute>
            <ShipmentRequests />
          </ProtectedRoute>
        }
      />
      
      {/* FOB Section Routes */}
      <Route
        path="/sections/fob"
        element={
          <ProtectedRoute>
            <FOBDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/inward-consignment"
        element={
          <ProtectedRoute>
            <FOBIncomingConsignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/bundle-receipt"
        element={
          <ProtectedRoute>
            <BundleReceiptEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/usn-tagging"
        element={
          <ProtectedRoute>
            <SortNoUsnTagging />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/assortment-packing"
        element={
          <ProtectedRoute>
            <FOBAssortmentPacking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/force-close"
        element={
          <ProtectedRoute>
            <FOBForceClose />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/damage-report"
        element={
          <ProtectedRoute>
            <FOBDamageReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/usn-reprint"
        element={
          <ProtectedRoute>
            <USNReprint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/internal-transfer"
        element={
          <ProtectedRoute>
            <InternalTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/report"
        element={
          <ProtectedRoute>
            <FOBSectionReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/closing-stock"
        element={
          <ProtectedRoute>
            <FOBClosingStockReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/fob/shipment-requests"
        element={
          <ProtectedRoute>
            <FOBShipmentRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer-requests"
        element={
          <ProtectedRoute>
            <TransferRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipped-transfers"
        element={
          <ProtectedRoute>
            <ShippedTransfers />
          </ProtectedRoute>
        }
      />

      {/* Greige Section Routes */}
      <Route
        path="/sections/greige"
        element={
          <ProtectedRoute>
            <GreigeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/inward-consignment"
        element={
          <ProtectedRoute>
            <GreigeIncomingConsignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/bundle-receipt"
        element={
          <ProtectedRoute>
            <GreigeBundleReceiptEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/bundle-split"
        element={
          <ProtectedRoute>
            <BundleSplit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/pinning"
        element={
          <ProtectedRoute>
            <GreigePinning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/marker-writing"
        element={
          <ProtectedRoute>
            <GreigeMarkerWriting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/packing"
        element={
          <ProtectedRoute>
            <GreigePacking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/sort-assignment"
        element={
          <ProtectedRoute>
            <SortAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/internal-transfer"
        element={
          <ProtectedRoute>
            <GreigeInternalTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/inspection"
        element={
          <ProtectedRoute>
            <GreigeInspection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/reconcile"
        element={
          <ProtectedRoute>
            <GreigeReconcile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/reconcile-report"
        element={
          <ProtectedRoute>
            <GreigeReconcileReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/legacy-bundle-backfill"
        element={
          <ProtectedRoute>
            <LegacyBundleBackfill />
          </ProtectedRoute>
        }
      />
      {/* Purchase Requisitions + Purchase Orders */}
      <Route path="/pr" element={<ProtectedRoute><PRList /></ProtectedRoute>} />
      <Route path="/pr/create" element={<ProtectedRoute><PRCreate /></ProtectedRoute>} />
      <Route path="/pr/:prId" element={<ProtectedRoute><PRDetail /></ProtectedRoute>} />
      <Route path="/pr/:prId/print" element={<ProtectedRoute><PRPrint /></ProtectedRoute>} />
      <Route path="/po" element={<ProtectedRoute><POList /></ProtectedRoute>} />
      <Route path="/po/create" element={<ProtectedRoute><POCreate /></ProtectedRoute>} />
      <Route path="/po/follow-up" element={<ProtectedRoute><POFollowUp /></ProtectedRoute>} />
      <Route path="/po/grn" element={<ProtectedRoute><GrnPage /></ProtectedRoute>} />
      <Route path="/po/:poId" element={<ProtectedRoute><PODetail /></ProtectedRoute>} />
      <Route
        path="/sections/greige/report"
        element={
          <ProtectedRoute>
            <GreigeSectionReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/closing-stock"
        element={
          <ProtectedRoute>
            <GreigeSectionClosingStock />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/shipment-requests"
        element={
          <ProtectedRoute>
            <GreigeShipmentRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/trace"
        element={
          <ProtectedRoute>
            <BundleUSNTrace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/bundle-detail-report"
        element={
          <ProtectedRoute>
            <GreigeBundleDetailReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige/documents-report"
        element={
          <ProtectedRoute>
            <GreigeDocumentsReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/trace"
        element={
          <ProtectedRoute>
            <BundleUSNTrace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/bundle-tracker"
        element={
          <ProtectedRoute>
            <GSBundleTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/doc-track"
        element={
          <ProtectedRoute>
            <GSDocTrack />
          </ProtectedRoute>
        }
      />

      {/* Greige Storage Section Routes */}
      <Route
        path="/sections/greige-storage"
        element={
          <ProtectedRoute>
            <GreigeStorageDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/inward-consignment"
        element={
          <ProtectedRoute>
            <GSIncomingConsignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/incoming-consignment"
        element={
          <ProtectedRoute>
            <GSIncomingConsignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/bundle-receipt"
        element={
          <ProtectedRoute>
            <GSBundleReceiptEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/put-away"
        element={
          <ProtectedRoute>
            <GSLocationPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/location-transfer"
        element={
          <ProtectedRoute>
            <GSLocationTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/shipment-request"
        element={
          <ProtectedRoute>
            <GSShipmentRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/shipment-requests"
        element={
          <ProtectedRoute>
            <GSShipmentRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/pick-slip"
        element={
          <ProtectedRoute>
            <GSPickSlipGeneration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/picking"
        element={
          <ProtectedRoute>
            <GSPicking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/sort-transfer"
        element={
          <ProtectedRoute>
            <GSSortNoTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/internal-transfer"
        element={
          <ProtectedRoute>
            <GSInternalTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/report"
        element={
          <ProtectedRoute>
            <GSReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/closing-stock"
        element={
          <ProtectedRoute>
            <GSClosingStockReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/location-setup"
        element={
          <ProtectedRoute>
            <GSLocationSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/aging-report"
        element={
          <ProtectedRoute>
            <StorageAgeingReport defaultTab="greige" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/assortment-pick-slip"
        element={
          <ProtectedRoute>
            <GSAssortmentPickSlip />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/assortment-picking"
        element={
          <ProtectedRoute>
            <GSAssortmentPicking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/available-pickers"
        element={
          <ProtectedRoute>
            <GSAvailablePickers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/location-stock-report"
        element={
          <ProtectedRoute>
            <GSLocationStockReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/internal-transfer-report"
        element={
          <ProtectedRoute>
            <GSInternalTransferReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/assortment-conversion-report"
        element={
          <ProtectedRoute>
            <GSAssortmentConversionReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/greige-storage/picked-not-transferred-report"
        element={
          <ProtectedRoute>
            <GSPickedNotTransferredReport />
          </ProtectedRoute>
        }
      />

      {/* Goods Transfer Section Routes */}
      <Route
        path="/sections/goods-transfer"
        element={
          <ProtectedRoute>
            <GoodsTransferDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/inward-consignment"
        element={
          <ProtectedRoute>
            <GoodsTransferIncomingConsignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/bundle-receipt"
        element={
          <ProtectedRoute>
            <GoodsTransferBundleReceiptEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/packing"
        element={
          <ProtectedRoute>
            <GTPacking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/sort-no-assignment"
        element={
          <ProtectedRoute>
            <SortNoAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/fg-assignment"
        element={
          <ProtectedRoute>
            <FGAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/location-assignment"
        element={
          <ProtectedRoute>
            <LocationAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/main-label-printing"
        element={
          <ProtectedRoute>
            <MainLabelPrinting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/print-job-history"
        element={
          <ProtectedRoute>
            <PrintJobHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/report"
        element={
          <ProtectedRoute>
            <GoodsTransferReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/closing-stock"
        element={
          <ProtectedRoute>
            <GoodsTransferClosingStock />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/shipment-requests"
        element={
          <ProtectedRoute>
            <GoodsTransferShipmentRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/label-reports"
        element={
          <ProtectedRoute>
            <LabelReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/internal-transfer"
        element={
          <ProtectedRoute>
            <GoodsTransferInternalTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/goods-transfer/location-transfer"
        element={
          <ProtectedRoute>
            <GTLocationTransfer />
          </ProtectedRoute>
        }
      />

      {/* Processing Section Routes */}
      <Route
        path="/sections/processing"
        element={
          <ProtectedRoute>
            <ProcessingDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/processing/inward-consignment"
        element={
          <ProtectedRoute>
            <ProcessingInwardConsignment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/processing/shipment-requests"
        element={
          <ProtectedRoute>
            <ProcessingShipmentRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/processing/internal-transfer"
        element={
          <ProtectedRoute>
            <ProcessingInternalTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/processing/report"
        element={
          <ProtectedRoute>
            <ProcessingReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/processing/closing-stock"
        element={
          <ProtectedRoute>
            <ProcessingClosingStock />
          </ProtectedRoute>
        }
      />
      <Route path="/sections/processing/units" element={<Navigate to="/sections/processing" replace />} />

      {/* Purchase Receipt against PO — every section module that raises POs */}
      <Route path="/sections/:sectionId/purchase-receipt" element={<ProtectedRoute><SectionPurchaseReceipt /></ProtectedRoute>} />
      <Route path="/sections/:sectionId/supplier-returns" element={<ProtectedRoute><SupplierReturns /></ProtectedRoute>} />
      {/* Dynamic Staff Performance route for all sections */}
      <Route
        path="/sections/:section/staff-performance"
        element={
          <ProtectedRoute>
            <StaffPerformanceDashboard isAdmin={false} />
          </ProtectedRoute>
        }
      />

      {/* Category Manager Module Routes */}
      <Route
        path="/category/incharge"
        element={
          <ProtectedRoute>
            <CategoryInchargeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/category/incharge/requests"
        element={
          <ProtectedRoute>
            <MyShipmentRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/category/manager"
        element={
          <ProtectedRoute>
            <CategoryManagerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipment-requests/section"
        element={
          <ProtectedRoute>
            <ShipmentRequestsSection />
          </ProtectedRoute>
        }
      />
      
      {/* Unified Dashboard — accessible to every authenticated user.
          Tile visibility is filtered per-user based on
          modules/sections/role/allowed_operations. Super Admin sees all. */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requireAdmin={true}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dc-notifications"
        element={
          <ProtectedRoute requireAdmin={true}>
            <DcNotificationSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import"
        element={
          <ProtectedRoute requireAdmin={true}>
            <ImportData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import-data"
        element={
          <ProtectedRoute requireAdmin={true}>
            <ImportData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manage-imports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <ManageImports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/database"
        element={
          <ProtectedRoute requireAdmin={true}>
            <DatabaseManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reassign-location"
        element={
          <ProtectedRoute requireAdmin={true}>
            <ReassignLocation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/label-config"
        element={
          <ProtectedRoute requireAdmin={true}>
            <LabelConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/main-label-templates"
        element={
          <ProtectedRoute requireAdmin={true}>
            <MainLabelTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/accounts/payslip"
        element={
          <ProtectedRoute>
            <Payslip />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/accounts/operation-rankings"
        element={
          <ProtectedRoute>
            <OperationRankings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/accounts/hourly-staff-report"
        element={
          <ProtectedRoute>
            <HourlyStaffReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/operations-manager"
        element={
          <ProtectedRoute requireAdmin={true}>
            <OperationsManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/print-stations"
        element={
          <ProtectedRoute requireAdmin={true}>
            <PrintStations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/print-settings"
        element={
          <ProtectedRoute requireAdmin={true}>
            <PrintSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inward-reports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <InwardReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/outward-reports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <OutwardReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customer-outward-reports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <CustomerOutwardReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transporter-returns"
        element={
          <ProtectedRoute>
            <TransporterReturns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/goods-transfer-reports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <GoodsTransferReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fob-reports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <FOBReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fob-entry-management"
        element={
          <ProtectedRoute requireAdmin={true}>
            <FOBEntryManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/backup"
        element={
          <ProtectedRoute requireAdmin={true}>
            <BackupRestore />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/goods-transfer-management"
        element={
          <ProtectedRoute requireAdmin={true}>
            <GoodsTransferManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inward-entry-management"
        element={
          <ProtectedRoute requireAdmin={true}>
            <InwardEntryManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/outward-entry-management"
        element={
          <ProtectedRoute requireAdmin={true}>
            <OutwardEntryManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sections/dispatch/transport-master"
        element={
          <ProtectedRoute>
            <TransportMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/greige-section-entry-management"
        element={
          <ProtectedRoute requireAdmin={true}>
            <GreigeSectionEntryManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/staff-performance"
        element={
          <ProtectedRoute requireAdmin={true}>
            <StaffPerformanceDashboard isAdmin={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/wms-reports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <WMSReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usn-tracker"
        element={
          <ProtectedRoute requireAdmin={true}>
            <USNTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usn-replacement-log"
        element={
          <ProtectedRoute requireAdmin={true}>
            <USNReplacementLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inhouse-bundle-reconciliation"
        element={
          <ProtectedRoute requireAdmin={true}>
            <InhouseBundleReconciliation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system-documentation"
        element={
          <ProtectedRoute requireAdmin={true}>
            <SystemDocumentation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usn-master"
        element={
          <ProtectedRoute requireAdmin={true}>
            <USNMasterSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usn-export"
        element={
          <ProtectedRoute requireAdmin={true}>
            <USNMasterExport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/all-usn-details"
        element={
          <ProtectedRoute requireAdmin={true}>
            <AllUSNDetailsReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/printer-settings"
        element={
          <ProtectedRoute requireAdmin={true}>
            <PrinterSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dispatch-reports"
        element={
          <ProtectedRoute requireAdmin={true}>
            <DispatchReportsAdmin />
          </ProtectedRoute>
        }
      />

      {/* WMS Routes */}
      <Route
        path="/wms"
        element={
          <ProtectedRoute>
            <WMSDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg"
        element={
          <ProtectedRoute>
            <SFGWMSDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg"
        element={
          <ProtectedRoute>
            <FGWMSDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/location-setup"
        element={
          <ProtectedRoute>
            <LocationSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/zone-mapping"
        element={
          <ProtectedRoute>
            <ZoneMapping />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg-putaway"
        element={
          <ProtectedRoute>
            <SFGPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/main-label-printing"
        element={
          <ProtectedRoute>
            <SFGMainLabelPrinting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/projections"
        element={
          <ProtectedRoute>
            <Projections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/pick-slips"
        element={
          <ProtectedRoute>
            <PickSlips />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/picker-guidance"
        element={
          <ProtectedRoute>
            <PickerGuidance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/replenishment"
        element={
          <ProtectedRoute>
            <ReplenishmentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/settings"
        element={
          <ProtectedRoute>
            <WMSSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg-putaway"
        element={
          <ProtectedRoute>
            <FGPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/put-away-supervisor"
        element={
          <ProtectedRoute>
            <FGPutAwaySupervisor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/put-away-slip-management"
        element={
          <ProtectedRoute>
            <FGPutAwaySlipManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/put-away-clearance"
        element={
          <ProtectedRoute>
            <FGPutAwayClearance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/create-pack"
        element={
          <ProtectedRoute>
            <FGCreatePack />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/small-box-putaway"
        element={
          <ProtectedRoute>
            <SmallBoxPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/fob-direct-put-away"
        element={
          <ProtectedRoute>
            <FOBDirectFGPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/put-away-mobile"
        element={
          <ProtectedRoute>
            <FGPutAwayMobile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/inventory-report"
        element={
          <ProtectedRoute>
            <FGInventoryReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/footprint-capacity"
        element={
          <ProtectedRoute>
            <FootprintCapacity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/put-away-entries"
        element={
          <ProtectedRoute>
            <PutAwayEntriesReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/inventory-report"
        element={
          <ProtectedRoute>
            <SFGInventoryReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/put-away-entries"
        element={
          <ProtectedRoute>
            <SFGPutAwayEntriesReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/create-pick-slip"
        element={
          <ProtectedRoute>
            <SFGCreatePickSlip />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/in-wip-stock-report"
        element={
          <ProtectedRoute>
            <InWIPStockReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/in-wip-bundle-status"
        element={
          <ProtectedRoute>
            <InWIPBundleStatusReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/floor-clearance"
        element={
          <ProtectedRoute>
            <SFGFloorClearance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/design-transfer"
        element={
          <ProtectedRoute>
            <SFGDesignTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/investigation-queue"
        element={
          <ProtectedRoute>
            <SFGInvestigationQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/investigation-queue"
        element={
          <ProtectedRoute>
            <FGInvestigationQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/usn-lookup"
        element={
          <ProtectedRoute>
            <USNLookup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/packing"
        element={
          <ProtectedRoute>
            <PackingDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/packing/*"
        element={
          <ProtectedRoute>
            <PackingWorkstation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/mobile/pick-and-pack"
        element={
          <ProtectedRoute>
            <MobilePickAndPack />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/pick-pack-live"
        element={
          <ProtectedRoute>
            <PickPackLiveBoard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/bundle-history"
        element={
          <ProtectedRoute>
            <BundleHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/mobile/cancelled-putaway"
        element={
          <ProtectedRoute>
            <CancelledPickSlipPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/order-management"
        element={
          <ProtectedRoute>
            <OrderManagementDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/bin-transfer"
        element={
          <ProtectedRoute>
            <BinToBinTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/sfg/bin-transfer"
        element={
          <ProtectedRoute>
            <SFGBinToBinTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/bin-audit"
        element={
          <ProtectedRoute>
            <FGBinAudit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/design-transfer"
        element={
          <ProtectedRoute>
            <FGDesignTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/design-transfer-report"
        element={
          <ProtectedRoute>
            <FGDesignTransferReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/usn-label-reprint"
        element={
          <ProtectedRoute>
            <FGUSNLabelReprint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/bulk-resticker"
        element={
          <ProtectedRoute>
            <BulkRestickerStaging />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/design-transfer-slips"
        element={
          <ProtectedRoute>
            <FGDesignTransferSlips />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/design-transfer-picker"
        element={
          <ProtectedRoute>
            <DesignTransferPicker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/storage-transfer"
        element={
          <ProtectedRoute>
            <WMSStorageTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg-pick-slips"
        element={
          <ProtectedRoute>
            <FGPickSlips />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/pick-slip-upload"
        element={
          <ProtectedRoute>
            <FGPickSlipUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal/pick-slip-upload"
        element={
          <ProtectedRoute>
            <SeasonalPickSlipUpload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal/packing-report"
        element={
          <ProtectedRoute>
            <SeasonalPackingReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/uploaded-pick-slips-report"
        element={
          <ProtectedRoute>
            <UploadedPickSlipsReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal/pending-packing-report"
        element={
          <ProtectedRoute>
            <SeasonalPendingPackingReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle"
        element={
          <ProtectedRoute>
            <SeasonalBundleDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/location-setup"
        element={
          <ProtectedRoute>
            <SeasonalLocationSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/put-away"
        element={
          <ProtectedRoute>
            <SeasonalPackedBoxPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/create-pick-slip"
        element={
          <ProtectedRoute>
            <SeasonalCreatePickSlip />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/available-pickers"
        element={
          <ProtectedRoute>
            <SeasonalAvailablePickers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/picking"
        element={
          <ProtectedRoute>
            <SeasonalMobilePicking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/stock-report"
        element={
          <ProtectedRoute>
            <SeasonalLocationStockReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/pick-slips"
        element={
          <ProtectedRoute>
            <SeasonalBoxPickSlips />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/seasonal-bundle/master-carton-history"
        element={
          <ProtectedRoute>
            <SeasonalMasterCartonHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg/packed-history"
        element={
          <ProtectedRoute>
            <FGPackedHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/fg-picker-guidance"
        element={
          <ProtectedRoute>
            <FGPickerGuidance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/design-transfer"
        element={
          <ProtectedRoute>
            <DesignTransfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/dispatch-supervisor"
        element={
          <ProtectedRoute>
            <DispatchSupervisor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/staff-rankings"
        element={
          <ProtectedRoute>
            <StaffRankings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/staff-rankings/tv"
        element={
          <StaffRankingsTV />
        }
      />
      <Route
        path="/reports/dispatch"
        element={
          <MobileStaffRankings />
        }
      />
      <Route
        path="/reports/internal-transfers"
        element={
          <ProtectedRoute>
            <InternalTransferReport backRoute="/" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/operation-wise-stock"
        element={
          <ProtectedRoute>
            <OperationWiseStockReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/storage-ageing"
        element={
          <ProtectedRoute>
            <StorageAgeingReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/dwell-time"
        element={
          <ProtectedRoute>
            <DwellTimeReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/fetch-document-number"
        element={
          <ProtectedRoute>
            <FetchDocumentNumber backRoute="/" />
          </ProtectedRoute>
        }
      />
      
      {/* Combo Product System Routes */}
      <Route
        path="/wms/combo/bom"
        element={
          <ProtectedRoute>
            <ComboBOMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo/assembly"
        element={
          <ProtectedRoute>
            <ComboAssemblyStation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo/reversal"
        element={
          <ProtectedRoute>
            <ComboReversal />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/wms/combo/bundle-trace"
        element={
          <ProtectedRoute>
            <ComboBundleTrace />
          </ProtectedRoute>
        }
      />

      {/* Combo V2 — Bulk Assembly */}
      <Route
        path="/wms/combo-v2"
        element={
          <ProtectedRoute>
            <ComboV2Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/planner"
        element={
          <ProtectedRoute>
            <ComboV2Planner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/plans"
        element={
          <ProtectedRoute>
            <ComboV2PlanList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/plans/:planId/print"
        element={
          <ProtectedRoute>
            <ComboV2PlanPrint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/plans/:planId"
        element={
          <ProtectedRoute>
            <ComboV2PlanDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/distributor"
        element={
          <ProtectedRoute>
            <ComboV2DistributorPlanList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/distributor/:planId"
        element={
          <ProtectedRoute>
            <ComboV2DistributorStation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/pp-station"
        element={
          <ProtectedRoute>
            <ComboV2PPStation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/print-desk"
        element={
          <ProtectedRoute>
            <ComboV2StickerPrintDesk />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/return-excess/:planId"
        element={
          <ProtectedRoute>
            <ComboV2ReturnExcess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo-v2/reversal"
        element={
          <ProtectedRoute>
            <ComboV2Reversal />
          </ProtectedRoute>
        }
      />

      {/* Combo Reconciliation (V1 + V2) */}
      <Route
        path="/wms/admin/combo-floor-reconciliation"
        element={
          <ProtectedRoute>
            <ComboFloorReconciliation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/admin/combo-successful-sessions"
        element={
          <ProtectedRoute>
            <ComboSuccessfulSessions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/admin/combo-variance/:sessionId"
        element={
          <ProtectedRoute>
            <ComboVarianceReconciliation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/combo/pp-staff-performance"
        element={
          <ProtectedRoute>
            <ComboPPStaffPerformance />
          </ProtectedRoute>
        }
      />

      <Route
        path="/wms/picker"
        element={
          <ProtectedRoute>
            <PickerDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* WMS Mobile Routes */}
      <Route
        path="/wms/mobile"
        element={
          <ProtectedRoute>
            <MobileWMSDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/mobile/sfg-putaway"
        element={
          <ProtectedRoute>
            <MobileSFGPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/mobile/sfg-picking"
        element={
          <ProtectedRoute>
            <MobileSFGPicking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/mobile/fg-putaway"
        element={
          <ProtectedRoute>
            <MobileFGPutAway />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/mobile/fg-picking"
        element={
          <ProtectedRoute>
            <MobileFGPicking />
          </ProtectedRoute>
        }
      />

      {/* Damage Clearance Routes */}
      <Route path="/wms/damage/clearance" element={<ProtectedRoute><WIPDamageClearance /></ProtectedRoute>} />
      <Route path="/wms/damage/receipt" element={<ProtectedRoute><DamageSectionReceipt /></ProtectedRoute>} />
      <Route path="/wms/damage/inventory" element={<ProtectedRoute><DamageInventoryReport /></ProtectedRoute>} />

      {/* ====== NEW SECTION ROUTES (16 Additional Sections) ====== */}
      
      {/* Inhouse Section Routes */}
      <Route path="/sections/inhouse" element={<ProtectedRoute><InhouseDashboard /></ProtectedRoute>} />
      <Route path="/sections/inhouse/internal-transfer" element={<ProtectedRoute><InhouseInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/inhouse/inward-consignment" element={<ProtectedRoute><InhouseInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/inhouse/bundle-receipt" element={<ProtectedRoute><InhouseBundleReceiptEntry /></ProtectedRoute>} />
      <Route path="/sections/inhouse/qty-confirmation" element={<ProtectedRoute><InhouseQtyConfirmation /></ProtectedRoute>} />
      <Route path="/sections/inhouse/processing-inward" element={<ProtectedRoute><ProcessingInward /></ProtectedRoute>} />
      <Route path="/sections/inhouse/processing-inward/history" element={<ProtectedRoute><ProcessingInwardHistory /></ProtectedRoute>} />
      <Route path="/sections/inhouse/folding" element={<ProtectedRoute><Folding /></ProtectedRoute>} />
      <Route path="/sections/inhouse/folding/history" element={<ProtectedRoute><FoldingHistory /></ProtectedRoute>} />
      <Route path="/sections/inhouse/pressing" element={<ProtectedRoute><Pressing /></ProtectedRoute>} />
      <Route path="/sections/inhouse/pressing/history" element={<ProtectedRoute><PressingHistory /></ProtectedRoute>} />
      <Route path="/sections/inhouse/damage-checking" element={<ProtectedRoute><DamageChecking /></ProtectedRoute>} />
      <Route path="/sections/inhouse/assortment-packing" element={<ProtectedRoute><AssortmentPacking /></ProtectedRoute>} />
      <Route path="/sections/inhouse/job-worker-return" element={<ProtectedRoute><JobWorkerReturn /></ProtectedRoute>} />
      <Route path="/sections/inhouse/create-job-order" element={<ProtectedRoute><InhouseCreateJobOrder /></ProtectedRoute>} />
      <Route path="/sections/inhouse/jobber-report" element={<ProtectedRoute><InhouseJobberReport /></ProtectedRoute>} />
      <Route path="/sections/inhouse/return-checks" element={<ProtectedRoute><InhouseReturnChecks /></ProtectedRoute>} />
      <Route path="/sections/inhouse/segregation" element={<ProtectedRoute><Segregation /></ProtectedRoute>} />
      <Route path="/sections/inhouse/design-transfer" element={<ProtectedRoute><InhouseDesignTransfer /></ProtectedRoute>} />
      <Route path="/sections/inhouse/direct-ship" element={<ProtectedRoute><AssortmentShip /></ProtectedRoute>} />
      <Route path="/sections/inhouse/direct-ship/history" element={<ProtectedRoute><DirectShipHistory /></ProtectedRoute>} />
      <Route path="/sections/inhouse/return-to-supplier/documents" element={<ProtectedRoute><ReturnToSupplierDocs /></ProtectedRoute>} />
      <Route path="/sections/inhouse/usn-report" element={<ProtectedRoute><InhouseUSNReport /></ProtectedRoute>} />
      <Route path="/sections/inhouse/operations-report" element={<ProtectedRoute><InhouseOperationsReport /></ProtectedRoute>} />
      <Route path="/sections/inhouse/re-folding-entry" element={<ProtectedRoute><ReFoldingEntry /></ProtectedRoute>} />
      <Route path="/sections/inhouse/color-assortment-entry" element={<ProtectedRoute><ColorAssortmentEntry /></ProtectedRoute>} />
      <Route path="/sections/fob/usn-report" element={<ProtectedRoute><FOBUSNReport /></ProtectedRoute>} />
      <Route path="/sections/inhouse/shipment-request" element={<ProtectedRoute><InhouseShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/inhouse/shipment-requests" element={<ProtectedRoute><InhouseShipmentRequest /></ProtectedRoute>} />

      {/* Unassorted Section Routes */}
      <Route path="/sections/unassorted" element={<ProtectedRoute><UnassortedDashboard /></ProtectedRoute>} />
      <Route path="/sections/unassorted/internal-transfer" element={<ProtectedRoute><UnassortedInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/unassorted/inward-consignment" element={<ProtectedRoute><UnassortedInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/unassorted/shipment-request" element={<ProtectedRoute><UnassortedShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/unassorted/shipment-requests" element={<ProtectedRoute><UnassortedShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/unassorted/bundle-receipt" element={<ProtectedRoute><UnassortedBundleReceiptEntry /></ProtectedRoute>} />

      {/* Tissue Section Routes */}
      <Route path="/sections/tissue" element={<ProtectedRoute><TissueDashboard /></ProtectedRoute>} />
      <Route path="/sections/tissue/hub" element={<ProtectedRoute><TissueHub /></ProtectedRoute>} />
      <Route path="/sections/tissue/jw-tagging/:jwId" element={<ProtectedRoute><TissueJwTaggingStation /></ProtectedRoute>} />
      <Route path="/sections/tissue/assortment" element={<ProtectedRoute><TissueAssortment /></ProtectedRoute>} />
      <Route path="/sections/tissue/jw/:jwId" element={<ProtectedRoute><TissueJwOrderDetail /></ProtectedRoute>} />
      <Route path="/sections/greige-storage/tissue-jw" element={<ProtectedRoute><GSTissueJwPicking /></ProtectedRoute>} />
      <Route path="/sections/greige-storage/processing-jo" element={<ProtectedRoute><GSProcessingJobOrders /></ProtectedRoute>} />
      <Route path="/sections/processing/job-orders" element={<ProtectedRoute><ProcessingJobOrders /></ProtectedRoute>} />
      <Route path="/sections/processing/job-orders/reports" element={<ProtectedRoute><ProcessingJobOrderReports /></ProtectedRoute>} />
      <Route path="/sections/processing/job-orders/:joId" element={<ProtectedRoute><ProcessingJobOrderDetail /></ProtectedRoute>} />
      <Route path="/sections/tissue/internal-transfer" element={<ProtectedRoute><TissueInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/tissue/inward-consignment" element={<ProtectedRoute><TissueInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/tissue/shipment-request" element={<ProtectedRoute><TissueShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/tissue/shipment-requests" element={<ProtectedRoute><TissueShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/tissue/bundle-receipt" element={<ProtectedRoute><TissueBundleReceiptEntry /></ProtectedRoute>} />

      {/* Flexi Section Routes */}
      <Route path="/sections/flexi" element={<ProtectedRoute><FlexiDashboard /></ProtectedRoute>} />
      <Route path="/sections/flexi/hub" element={<ProtectedRoute><FlexiHub /></ProtectedRoute>} />
      <Route path="/sections/flexi/jw-tagging/:jwId" element={<ProtectedRoute><FlexiJwTaggingStation /></ProtectedRoute>} />
      <Route path="/sections/flexi/receipt" element={<ProtectedRoute><FlexiJwReceiptStation /></ProtectedRoute>} />
      <Route path="/sections/flexi/jw-report" element={<ProtectedRoute><FlexiJwOrderReport /></ProtectedRoute>} />
      <Route path="/sections/flexi/assortment" element={<ProtectedRoute><FlexiAssortment /></ProtectedRoute>} />
      <Route path="/sections/flexi/internal-transfer" element={<ProtectedRoute><FlexiInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/flexi/inward-consignment" element={<ProtectedRoute><FlexiInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/flexi/shipment-request" element={<ProtectedRoute><FlexiShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/flexi/shipment-requests" element={<ProtectedRoute><FlexiShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/flexi/bundle-receipt" element={<ProtectedRoute><FlexiBundleReceiptEntry /></ProtectedRoute>} />
      <Route path="/sections/flexi/fg-assignment" element={<ProtectedRoute><FlexiFGAssignment /></ProtectedRoute>} />

      {/* Shirt Manufacturing Section Routes */}
      <Route path="/sections/shirt-manufacturing" element={<ProtectedRoute><ShirtManufacturingDashboard /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/internal-transfer" element={<ProtectedRoute><ShirtManufacturingInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/inward-consignment" element={<ProtectedRoute><ShirtManufacturingInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/shipment-request" element={<ProtectedRoute><ShirtManufacturingShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/shipment-requests" element={<ProtectedRoute><ShirtManufacturingShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/bundle-receipt" element={<ProtectedRoute><ShirtManufacturingBundleReceiptEntry /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/hub" element={<ProtectedRoute><ShirtMfgHub /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/jw-tagging/:jwId" element={<ProtectedRoute><ShirtMfgJwTaggingStation /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/shirts-assortment" element={<ProtectedRoute><ShirtsAssortment /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/bundle-split" element={<ProtectedRoute><ShirtMfgBundleSplit /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/qty-inspection" element={<ProtectedRoute><ShirtMfgQtyInspection /></ProtectedRoute>} />
      <Route path="/sections/shirt-manufacturing/variance-report" element={<ProtectedRoute><ShirtMfgVarianceReport /></ProtectedRoute>} />

      {/* Sample Section Routes */}
      <Route path="/sections/sample" element={<ProtectedRoute><SampleDashboard /></ProtectedRoute>} />
      <Route path="/sections/sample/internal-transfer" element={<ProtectedRoute><SampleInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/sample/inward-consignment" element={<ProtectedRoute><SampleInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/sample/shipment-request" element={<ProtectedRoute><SampleShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/sample/shipment-requests" element={<ProtectedRoute><SampleShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/sample/bundle-receipt" element={<ProtectedRoute><SampleBundleReceiptEntry /></ProtectedRoute>} />
      <Route path="/sections/sample/pick-slip-upload" element={<ProtectedRoute><SamplePickSlipUpload /></ProtectedRoute>} />
      <Route path="/sections/sample/cutting-station" element={<ProtectedRoute><SampleCuttingStation /></ProtectedRoute>} />
      <Route path="/sections/sample/full-sample-packing" element={<ProtectedRoute><SampleFullSamplePacking /></ProtectedRoute>} />

      {/* Sales Return Section Routes */}
      <Route path="/sections/sales-return" element={<ProtectedRoute><SalesReturnDashboard /></ProtectedRoute>} />
      <Route path="/sections/sales-return/internal-transfer" element={<ProtectedRoute><SalesReturnInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/sales-return/inward-consignment" element={<ProtectedRoute><SalesReturnInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/sales-return/shipment-request" element={<ProtectedRoute><SalesReturnShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/sales-return/shipment-requests" element={<ProtectedRoute><SalesReturnShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/sales-return/bundle-receipt" element={<ProtectedRoute><SalesReturnBundleReceiptEntry /></ProtectedRoute>} />
      <Route path="/sections/sales-return/transactions" element={<ProtectedRoute><SalesReturnTransactions /></ProtectedRoute>} />
      <Route path="/sections/sales-return/refinish-bundles" element={<ProtectedRoute><SalesReturnRefinishBundleManager /></ProtectedRoute>} />

      {/* Accounts Section Routes */}
      <Route path="/sections/accounts" element={<ProtectedRoute><AccountsDashboard /></ProtectedRoute>} />
      <Route path="/sections/accounts/internal-transfer" element={<ProtectedRoute><AccountsInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/accounts/inward-consignment" element={<ProtectedRoute><AccountsInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/accounts/shipment-request" element={<ProtectedRoute><AccountsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/accounts/shipment-requests" element={<ProtectedRoute><AccountsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/accounts/bundle-receipt" element={<ProtectedRoute><AccountsBundleReceiptEntry /></ProtectedRoute>} />
      <Route path="/sections/accounts/hr" element={<ProtectedRoute><AccountsHRTile /></ProtectedRoute>} />
      <Route path="/sections/accounts/gate-entry-details" element={<ProtectedRoute><AccountsGateEntryDetails /></ProtectedRoute>} />
      <Route path="/sections/accounts/outward-gate-entries/customer" element={<ProtectedRoute><OutwardGateEntryDetails type="customer" /></ProtectedRoute>} />
      <Route path="/sections/accounts/outward-gate-entries/regular" element={<ProtectedRoute><OutwardGateEntryDetails type="regular" /></ProtectedRoute>} />
      <Route path="/hr/folding-category-wages" element={<ProtectedRoute><FoldingCategoryWages /></ProtectedRoute>} />

      {/* Trims Section Routes */}
      <Route path="/sections/trims" element={<ProtectedRoute><TrimsDashboard /></ProtectedRoute>} />
      <Route path="/sections/trims/internal-transfer" element={<ProtectedRoute><TrimsInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/trims/inward-consignment" element={<ProtectedRoute><TrimsInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/trims/shipment-request" element={<ProtectedRoute><TrimsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/trims/shipment-requests" element={<ProtectedRoute><TrimsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/trims/bundle-receipt" element={<ProtectedRoute><TrimsBundleReceiptEntry /></ProtectedRoute>} />

      {/* House Keeping Section Routes */}
      <Route path="/sections/house-keeping" element={<ProtectedRoute><HouseKeepingDashboard /></ProtectedRoute>} />
      <Route path="/sections/house-keeping/internal-transfer" element={<ProtectedRoute><HouseKeepingInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/house-keeping/inward-consignment" element={<ProtectedRoute><HouseKeepingInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/house-keeping/shipment-request" element={<ProtectedRoute><HouseKeepingShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/house-keeping/shipment-requests" element={<ProtectedRoute><HouseKeepingShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/house-keeping/bundle-receipt" element={<ProtectedRoute><HouseKeepingBundleReceiptEntry /></ProtectedRoute>} />

      {/* Electrical Goods Section Routes */}
      <Route path="/sections/electrical-goods" element={<ProtectedRoute><ElectricalGoodsDashboard /></ProtectedRoute>} />
      <Route path="/sections/electrical-goods/internal-transfer" element={<ProtectedRoute><ElectricalGoodsInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/electrical-goods/inward-consignment" element={<ProtectedRoute><ElectricalGoodsInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/electrical-goods/shipment-request" element={<ProtectedRoute><ElectricalGoodsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/electrical-goods/shipment-requests" element={<ProtectedRoute><ElectricalGoodsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/electrical-goods/bundle-receipt" element={<ProtectedRoute><ElectricalGoodsBundleReceiptEntry /></ProtectedRoute>} />

      {/* IT Goods Section Routes */}
      <Route path="/sections/it-goods" element={<ProtectedRoute><ITGoodsDashboard /></ProtectedRoute>} />
      <Route path="/sections/it-goods/internal-transfer" element={<ProtectedRoute><ITGoodsInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/it-goods/inward-consignment" element={<ProtectedRoute><ITGoodsInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/it-goods/shipment-request" element={<ProtectedRoute><ITGoodsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/it-goods/shipment-requests" element={<ProtectedRoute><ITGoodsShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/it-goods/bundle-receipt" element={<ProtectedRoute><ITGoodsBundleReceiptEntry /></ProtectedRoute>} />

      {/* R&M (Repair & Maintenance) Section Routes */}
      <Route path="/sections/rnm" element={<ProtectedRoute><RnMDashboard /></ProtectedRoute>} />
      <Route path="/sections/rnm/internal-transfer" element={<ProtectedRoute><RnMInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/rnm/inward-consignment" element={<ProtectedRoute><RnMInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/rnm/shipment-request" element={<ProtectedRoute><RnMShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/rnm/shipment-requests" element={<ProtectedRoute><RnMShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/rnm/bundle-receipt" element={<ProtectedRoute><RnMBundleReceiptEntry /></ProtectedRoute>} />

      {/* ECOM Section Routes */}
      <Route path="/sections/ecom" element={<ProtectedRoute><ECOMDashboard /></ProtectedRoute>} />
      <Route path="/sections/ecom/internal-transfer" element={<ProtectedRoute><ECOMInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/ecom/inward-consignment" element={<ProtectedRoute><ECOMInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/ecom/shipment-request" element={<ProtectedRoute><ECOMShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/ecom/shipment-requests" element={<ProtectedRoute><ECOMShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/ecom/bundle-receipt" element={<ProtectedRoute><ECOMBundleReceiptEntry /></ProtectedRoute>} />

      {/* Admin Section Routes */}
      <Route path="/sections/admin-section" element={<ProtectedRoute><AdminSectionDashboard /></ProtectedRoute>} />
      <Route path="/sections/admin-section/internal-transfer" element={<ProtectedRoute><AdminSectionInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/admin-section/inward-consignment" element={<ProtectedRoute><AdminSectionInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/admin-section/shipment-request" element={<ProtectedRoute><AdminSectionShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/admin-section/shipment-requests" element={<ProtectedRoute><AdminSectionShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/admin-section/bundle-receipt" element={<ProtectedRoute><AdminSectionBundleReceiptEntry /></ProtectedRoute>} />

      {/* Weaving Section Routes */}
      <Route path="/sections/weaving" element={<ProtectedRoute><WeavingDashboard /></ProtectedRoute>} />
      <Route path="/sections/weaving/internal-transfer" element={<ProtectedRoute><WeavingInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/weaving/inward-consignment" element={<ProtectedRoute><WeavingInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/weaving/shipment-request" element={<ProtectedRoute><WeavingShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/weaving/shipment-requests" element={<ProtectedRoute><WeavingShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/weaving/bundle-receipt" element={<ProtectedRoute><WeavingBundleReceiptEntry /></ProtectedRoute>} />

      {/* Yarn Section Routes */}
      <Route path="/sections/yarn" element={<ProtectedRoute><YarnDashboard /></ProtectedRoute>} />
      <Route path="/sections/yarn/internal-transfer" element={<ProtectedRoute><YarnInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/yarn/inward-consignment" element={<ProtectedRoute><YarnInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/yarn/shipment-request" element={<ProtectedRoute><YarnShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/yarn/shipment-requests" element={<ProtectedRoute><YarnShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/yarn/bundle-receipt" element={<ProtectedRoute><YarnBundleReceiptEntry /></ProtectedRoute>} />

      {/* Dispatch Section Routes */}
      <Route path="/sections/dispatch" element={<ProtectedRoute><DispatchDashboard /></ProtectedRoute>} />
      <Route path="/sections/dispatch/internal-transfer" element={<ProtectedRoute><DispatchInternalTransfer /></ProtectedRoute>} />
      <Route path="/sections/dispatch/inward-consignment" element={<ProtectedRoute><DispatchInwardConsignment /></ProtectedRoute>} />
      <Route path="/sections/dispatch/shipment-request" element={<ProtectedRoute><DispatchShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/dispatch/shipment-requests" element={<ProtectedRoute><DispatchShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/dispatch/bundle-receipt" element={<ProtectedRoute><DispatchBundleReceiptEntry /></ProtectedRoute>} />
      <Route path="/sections/dispatch/pieces-processed" element={<ProtectedRoute><DispatchPiecesProcessed /></ProtectedRoute>} />
      <Route path="/sections/dispatch/pieces-processed/tv" element={<DispatchPiecesProcessedTV />} />
      <Route path="/sections/dispatch/usn-tracking" element={<ProtectedRoute><DispatchUSNTracking /></ProtectedRoute>} />
      <Route path="/sections/dispatch/bundle-receipt-report" element={<ProtectedRoute><BundleReceiptReport /></ProtectedRoute>} />
      <Route path="/sections/dispatch/internal-transfer-report" element={<ProtectedRoute><DispatchInternalTransferReport /></ProtectedRoute>} />
      <Route path="/sections/dispatch/footprint-movement-report" element={<ProtectedRoute><FootprintMovementReport /></ProtectedRoute>} />
      <Route path="/sections/dispatch/traffic-control" element={<ProtectedRoute><TrafficControlDashboard /></ProtectedRoute>} />

      {/* === Phase B — Per-section Dispatch Hubs (color_combo / art_silk / kids) === */}
      {/* Catch-all comes AFTER the legacy fixed paths so Salem's flow is untouched. */}
      <Route path="/sections/dispatch/:section" element={<ProtectedRoute><PerSectionDispatchHub /></ProtectedRoute>} />
      {/* Phase D Slice 1 — Bundle Receipt Entry clone (Salem-untouched). Must come BEFORE the catch-all stub. */}
      <Route path="/sections/dispatch/:section/bundle-receipt" element={<ProtectedRoute><PerSectionBundleReceiptEntryClone /></ProtectedRoute>} />
      {/* Phase D Slice 2 — Pieces Processed clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/pieces-processed" element={<ProtectedRoute><PerSectionPiecesProcessedClone /></ProtectedRoute>} />
      {/* Phase D Slice 3 — Inward Consignment clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/inward-consignment" element={<ProtectedRoute><PerSectionInwardConsignmentClone /></ProtectedRoute>} />
      {/* Phase D Slice 4 — Internal Transfer clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/internal-transfer" element={<ProtectedRoute><PerSectionInternalTransferClone /></ProtectedRoute>} />
      {/* Phase D Slice 5 — SFG Put-Away clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/sfg-put-away" element={<ProtectedRoute><PerSectionSfgPutAwayClone /></ProtectedRoute>} />
      {/* Phase D Slice 6 — SFG Pick (manual) clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/sfg-pick" element={<ProtectedRoute><PerSectionSfgPickClone /></ProtectedRoute>} />
      {/* Phase D Slice 7 — FG Put-Away clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/fg-put-away" element={<ProtectedRoute><PerSectionFgPutAwayClone /></ProtectedRoute>} />
      {/* Phase D Slice 8 — FG Pick (manual) clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/fg-pick" element={<ProtectedRoute><PerSectionFgPickClone /></ProtectedRoute>} />
      {/* Phase D Slice 9 (Phase 1) — ComboV2 Plans + Distribute clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/combo-v2" element={<ProtectedRoute><PerSectionComboV2Clone /></ProtectedRoute>} />
      {/* Phase D Slice 9.1 (full Salem parity) — Matrix Bulk Planner per-section. */}
      <Route path="/sections/dispatch/:section/combo-v2-planner" element={<ProtectedRoute><PerSectionComboV2Planner /></ProtectedRoute>} />
      {/* Phase D Slice 9.2 — ComboV2 PP Station scan-USN clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/pp-station" element={<ProtectedRoute><PerSectionPpStationClone /></ProtectedRoute>} />
      {/* Phase D Slice 9.3 — ComboV2 Return Excess clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/return-excess" element={<ProtectedRoute><PerSectionReturnExcessClone /></ProtectedRoute>} />
      {/* Phase D Slice 9.4 — ComboV2 Reversal clone (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/combo-reversal" element={<ProtectedRoute><PerSectionReversalClone /></ProtectedRoute>} />
      {/* Phase I — Per-section WMS Mobile Dashboard (Salem-untouched clone of /wms/mobile). */}
      <Route path="/sections/dispatch/:section/mobile" element={<ProtectedRoute><PerSectionMobileDashboard /></ProtectedRoute>} />
      {/* Phase O — Per-section mobile sub-pages (Salem-untouched clones of MobileSFG/FG put-away/pick). */}
      <Route path="/sections/dispatch/:section/mobile/sfg-put-away" element={<ProtectedRoute><PerSectionMobileSfgPutAway /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/mobile/sfg-pick" element={<ProtectedRoute><PerSectionMobileSfgPick /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/mobile/fg-put-away" element={<ProtectedRoute><PerSectionMobileFgPutAway /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/mobile/fg-pick" element={<ProtectedRoute><PerSectionMobileFgPick /></ProtectedRoute>} />
      {/* Phase P — Per-section section landings, shipment request, footprint movement report (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/sfg-wms" element={<ProtectedRoute><PerSectionSfgWmsLanding /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/fg-wms" element={<ProtectedRoute><PerSectionFgWmsLanding /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/shipment-request" element={<ProtectedRoute><PerSectionShipmentRequest /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/footprint-movement-report" element={<ProtectedRoute><PerSectionFootprintMovementReport /></ProtectedRoute>} />
      {/* Phase Q — Per-section Dispatch Supervisor (Salem-untouched). */}
      <Route path="/sections/dispatch/:section/dispatch-supervisor" element={<ProtectedRoute><PerSectionDispatchSupervisor /></ProtectedRoute>} />
      {/* Phase R — Per-section WMS Setup (locations, racks, levels, zones, settings, footprint, bin transfers, bin audit). */}
      <Route path="/sections/dispatch/:section/wms-setup/location-setup" element={<ProtectedRoute><PerSectionLocationSetup /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/wms-setup/zone-mapping" element={<ProtectedRoute><PerSectionZoneMapping /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/wms-setup/settings" element={<ProtectedRoute><PerSectionWMSSettings /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/wms-setup/footprint-capacity" element={<ProtectedRoute><PerSectionFootprintCapacity /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/wms-setup/bin-to-bin-transfer" element={<ProtectedRoute><PerSectionBinToBinTransfer /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/wms-setup/sfg-bin-to-bin-transfer" element={<ProtectedRoute><PerSectionSFGBinToBinTransfer /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/wms-setup/fg-bin-audit" element={<ProtectedRoute><PerSectionFGBinAudit /></ProtectedRoute>} />
      {/* FG Re-Sticker Queue — mounted both at admin level (Salem) and per-section. */}
      <Route path="/admin/re-sticker-queue" element={<ProtectedRoute><ReStickerQueue /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/re-sticker" element={<ProtectedRoute><ReStickerQueue /></ProtectedRoute>} />
      {/* Phase S — per-section dispatch reports (Option A: same component, section-aware via useParams) */}
      <Route path="/sections/dispatch/:section/traffic-control" element={<ProtectedRoute><TrafficControlDashboard /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/usn-tracking" element={<ProtectedRoute><DispatchUSNTracking /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/internal-transfer-report" element={<ProtectedRoute><DispatchInternalTransferReport /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/bundle-receipt-report" element={<ProtectedRoute><BundleReceiptReport /></ProtectedRoute>} />
      <Route path="/sections/dispatch/:section/:screen" element={<ProtectedRoute><PerSectionStub /></ProtectedRoute>} />

      {/* Misc Outward — available for all sections */}
      <Route path="/sections/:sectionId/misc-outward" element={<ProtectedRoute><MiscOutward /></ProtectedRoute>} />
      <Route path="/sections/:sectionId/it-report" element={<ProtectedRoute><SectionITReport /></ProtectedRoute>} />
      <Route path="/sections/:sectionId/documents-report" element={<ProtectedRoute><SectionDocumentsReport /></ProtectedRoute>} />
      <Route path="/sections/dispatch/it-report" element={<ProtectedRoute><SectionITReport sectionId="dispatch" /></ProtectedRoute>} />
      <Route path="/sections/dispatch/documents-report" element={<ProtectedRoute><SectionDocumentsReport sectionId="dispatch" /></ProtectedRoute>} />
      <Route path="/sections/fob/packed-not-transferred" element={<ProtectedRoute><FOBPackedNotTransferredReport /></ProtectedRoute>} />
      <Route path="/sections/fob/hourly-report" element={<ProtectedRoute><HourlyStaffReport lockModule="FOB" backRoute="/sections/fob" backLabel="FOB Section" /></ProtectedRoute>} />
      <Route path="/sections/inhouse/hourly-report" element={<ProtectedRoute><HourlyStaffReport lockModule="Inhouse" backRoute="/sections/inhouse" backLabel="Inhouse Section" /></ProtectedRoute>} />
      <Route path="/sections/processing/hourly-report" element={<ProtectedRoute><HourlyStaffReport lockModule="Processing" backRoute="/sections/processing" backLabel="Processing Section" /></ProtectedRoute>} />
      <Route path="/sections/dispatch/hourly-report" element={<ProtectedRoute><HourlyStaffReport lockModule="Dispatch" backRoute="/sections/dispatch" backLabel="Dispatch Section" /></ProtectedRoute>} />
      <Route path="/sections/goods-transfer/hourly-report" element={<ProtectedRoute><HourlyStaffReport lockModule="Goods Transfer" backRoute="/sections/goods-transfer" backLabel="Goods Transfer" /></ProtectedRoute>} />
      <Route path="/sections/fob/operation-rankings" element={<ProtectedRoute><OperationRankings lockModule="FOB" backRoute="/sections/fob" backLabel="FOB Section" /></ProtectedRoute>} />
      <Route path="/sections/inhouse/operation-rankings" element={<ProtectedRoute><OperationRankings lockModule="Inhouse" backRoute="/sections/inhouse" backLabel="Inhouse Section" /></ProtectedRoute>} />
      <Route path="/sections/processing/operation-rankings" element={<ProtectedRoute><OperationRankings lockModule="Processing" backRoute="/sections/processing" backLabel="Processing Section" /></ProtectedRoute>} />
      <Route path="/sections/dispatch/operation-rankings" element={<ProtectedRoute><OperationRankings lockModule="Dispatch" backRoute="/sections/dispatch" backLabel="Dispatch Section" /></ProtectedRoute>} />
      <Route path="/sections/goods-transfer/operation-rankings" element={<ProtectedRoute><OperationRankings lockModule="Goods Transfer" backRoute="/sections/goods-transfer" backLabel="Goods Transfer" /></ProtectedRoute>} />
      <Route path="/sections/:sectionId/inventory-report" element={<ProtectedRoute><SectionInventoryReport /></ProtectedRoute>} />

      {/* Internal Transport Section Routes (Phase 1 MVP) */}
      <Route path="/sections/transport" element={<ProtectedRoute><TransportDashboard /></ProtectedRoute>} />
      <Route path="/sections/transport/my-tasks" element={<ProtectedRoute><TransportMyTasks /></ProtectedRoute>} />
      <Route path="/sections/transport/requests" element={<ProtectedRoute><TransportRequestBoard /></ProtectedRoute>} />
      <Route path="/sections/transport/request-vehicle" element={<ProtectedRoute><TransportRequestVehicle /></ProtectedRoute>} />
      <Route path="/sections/transport/dispatch" element={<ProtectedRoute><TransportDispatchBoard /></ProtectedRoute>} />
      <Route path="/sections/transport/locations" element={<ProtectedRoute><TransportLocations /></ProtectedRoute>} />
      <Route path="/sections/transport/trips" element={<ProtectedRoute><TransportTrips /></ProtectedRoute>} />
      <Route path="/sections/transport/fuel" element={<ProtectedRoute><TransportFuelLog /></ProtectedRoute>} />
      <Route path="/sections/transport/service" element={<ProtectedRoute><TransportServiceLog /></ProtectedRoute>} />
      <Route path="/sections/transport/attendance" element={<ProtectedRoute><TransportAttendance /></ProtectedRoute>} />
      <Route path="/sections/transport/vehicles" element={<ProtectedRoute><TransportVehicles /></ProtectedRoute>} />
      <Route path="/sections/transport/drivers" element={<ProtectedRoute><TransportDrivers /></ProtectedRoute>} />
      <Route path="/sections/transport/reports" element={<ProtectedRoute><TransportReports /></ProtectedRoute>} />
      <Route path="/sections/transport/settings" element={<ProtectedRoute><TransportSettings /></ProtectedRoute>} />

    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LabelPrinterProvider>
          <AppRouter />
        </LabelPrinterProvider>
      </BrowserRouter>
      <Toaster
        position="bottom-center"
        expand={true}
        richColors
        closeButton
      />
      <USNReplacementDialog />
    </div>
  );
}

export default App;
